import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionInspection, 
  InspectionStats, 
  InspectionFilters 
} from '../types/construction-inspections.types';

export const ConstructionInspectionsService = {
  /**
   * Danh sách phiếu kiểm tra thi công
   */
  async getInspections(companyId: string, filters: InspectionFilters = {}): Promise<{ data: ConstructionInspection[], total: number }> {
    let query = supabase
      .from('construction_inspections')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        inspector:user_profiles!inspector_id(id, full_name, avatar_url:avatar_path),
        requester:user_profiles!requested_by(id, full_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('inspection_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.inspectorId) query = query.eq('inspector_id', filters.inspectorId);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,inspection_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionInspection[],
      total: count || 0
    };
  },

  /**
   * Chi tiết phiếu kiểm tra
   */
  async getInspectionDetail(id: string): Promise<ConstructionInspection> {
    const { data, error } = await supabase
      .from('construction_inspections')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        inspector:user_profiles!inspector_id(id, full_name, avatar_url:avatar_path),
        requester:user_profiles!requested_by(id, full_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionInspection;
  },

  /**
   * Thống kê kiểm tra
   */
  async getInspectionStats(companyId: string, projectId?: string): Promise<InspectionStats> {
    let query = supabase.from('construction_inspections').select('status_code, result_code, score').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const passed = data.filter(i => i.result_code === 'pass').length;
    const failed = data.filter(i => i.result_code === 'fail').length;
    const pending = data.filter(i => i.status_code === 'requested' || i.status_code === 'scheduled').length;
    const avgScore = data.length > 0 ? data.reduce((sum, i) => sum + (i.score || 0), 0) / data.length : 0;

    return {
      totalInspections: data.length,
      passedCount: passed,
      failedCount: failed,
      pendingCount: pending,
      avgScore,
    };
  },

  /**
   * CRUD
   */
  async createInspection(data: Partial<ConstructionInspection>): Promise<ConstructionInspection> {
    const { data: result, error } = await supabase
      .from('construction_inspections')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionInspection;
  },

  async updateInspection(id: string, updates: Partial<ConstructionInspection>): Promise<void> {
    const { error } = await supabase
      .from('construction_inspections')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
